import { AnsweredQuestion, DeleteQuestionComment, DeleteType, Message, QuestionBan } from "@/types/socket-messages";
import { PaginatedQuestion, QuestionComment, Status } from "@/types/working-model";
import { isObject } from "underscore";

type QuestionStatusData = { id: number; status: Status };
type QuestionLikeData = { id?: number; question_id: number };

export function assertIsPaginatedQuestion(data: unknown): asserts data is PaginatedQuestion {
	if (!isObject(data)) {
		throw new TypeError(`PaginatedQuestion data received was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('id' in data) || isNaN(data.id)) {
		throw new TypeError(`PaginatedQuestion invalid id field ${data?.id}`);
	}
	if (!('question' in data) || typeof data.question !== 'string') {
		throw new TypeError(`PaginatedQuestion invalid question field ${data?.question}`);
	}
	if (!('status' in data) || isNaN(data.status)) {
		throw new TypeError(`PaginatedQuestion invalid status field ${data?.status}`);
	}
	if (!('bl_profile' in data) && !('bl_profile_non_registered' in data)) {
		throw new TypeError(`PaginatedQuestion requires either bl_profile or bl_profile_non_registered`);
	}
	if (data?.bl_profile !== null && isNaN(data.bl_profile)) {
		throw new TypeError(`PaginatedQuestion invalid bl_profile. Expected number, received ${typeof data.bl_profile}`);
	}
	if (data?.bl_profile_non_registered !== null && isNaN(data.bl_profile_non_registered)) {
		throw new TypeError(`PaginatedQuestion invalid bl_profile_non_registered. Expected number, received ${typeof data.bl_profile_non_registered}`);
	}
	if (!('event' in data) || isNaN(data.event)) {
		throw new TypeError(`PaginatedQuestion invalid event field ${data?.event}`);
	}
	if (!('channel' in data) || isNaN(data.channel)) {
		throw new TypeError(`PaginatedQuestion invalid channel field ${data?.channel}`);
	}
	if (!('session' in data) || isNaN(data.session)) {
		throw new TypeError(`PaginatedQuestion invalid session field ${data?.session}`);
	}
	if (!('module_id' in data) || isNaN(data.module_id)) {
		throw new TypeError(`PaginatedQuestion invalid module_id field ${data?.module_id}`);
	}
}

export function assertIsQuestionSocketDeleteType(data: unknown): asserts data is DeleteType {
	if (!isObject(data)) {
		throw new TypeError(`DeleteType data received was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('id' in data)) {
		throw new TypeError('DeleteType data missing id field');
	}
	if (isNaN(data.id)) {
		throw new TypeError(`DeleteType id was not of the correct type. Expected a number, received ${typeof data.id}`);
	}
}

export function assertIsDeleteQuestionComment(data: unknown): asserts data is DeleteQuestionComment {
	if (!isObject(data)) {
		throw new TypeError(`DeleteQuestionComment data was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('comment_id' in data) || isNaN(data.comment_id)) {
		throw new TypeError(`DeleteQuestionComment invalid comment_id field ${data?.comment_id}`);
	}
	if (!('question_id' in data) || isNaN(data.question_id)) {
		throw new TypeError(`DeleteQuestionComment invalid question_id field ${data?.question_id}`);
	}
	if (!('module_id' in data)) {
		throw new TypeError('DeleteQuestionComment missing module_id field');
	}
}

export function assertIsAnsweredQuestion(data: unknown): asserts data is AnsweredQuestion {
	if (!isObject(data)) {
		throw new TypeError(`AnsweredQuestion data was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('question_ids' in data) || !Array.isArray(data.question_ids)) {
		throw new TypeError(`AnsweredQuestion invalid question_ids field ${data?.question_ids}`);
	}
	if (!('answered' in data) || typeof data.answered !== 'boolean') {
		throw new TypeError(`AnsweredQuestion invalid answered field ${data?.answered}`);
	}
}

export function assertIsQuestionBan(data: unknown): asserts data is QuestionBan {
	if (!isObject(data)) {
		throw new TypeError(`QuestionBan data was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('type' in data) || (data.type !== 'question' && data.type !== 'question_comment')) {
		throw new TypeError(`QuestionBan invalid type field ${data?.type}`);
	}
	if (!('id' in data) || isNaN(data.id)) {
		throw new TypeError(`QuestionBan invalid id field ${data?.id}`);
	}
	if (!('question_id' in data) || isNaN(data.question_id)) {
		throw new TypeError(`QuestionBan invalid question_id field ${data?.question_id}`);
	}
}

export function assertIsSocketQuestionComment(data: unknown): asserts data is QuestionComment {
	if (!isObject(data)) {
		throw new TypeError(`SocketQuestionComment data was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('channel' in data) || isNaN(data.channel)) {
		throw new TypeError(`SocketQuestionComment invalid channel field ${data?.channel}`);
	}
	if (!('session' in data) || isNaN(data.session)) {
		throw new TypeError(`SocketQuestionComment invalid session field ${data?.session}`);
	}
	if (!('event' in data) || isNaN(data.event)) {
		throw new TypeError(`SocketQuestionComment invalid event field ${data?.event}`);
	}
	if (!('id' in data) || isNaN(data.id)) {
		throw new TypeError(`SocketQuestionComment invalid id field ${data?.id}`);
	}
	if (!('question_id' in data) || isNaN(data.question_id)) {
		throw new TypeError(`SocketQuestionComment invalid question_id field ${data?.question_id}`);
	}
	if (!('comment' in data) || typeof data.comment !== 'string') {
		throw new TypeError(`SocketQuestionComment invalid comment field ${data?.comment}`);
	}
	if (!('bl_profile' in data) && !('bl_profile_non_registered' in data)) {
		throw new TypeError(`SocketQuestionComment requires either bl_profile or bl_profile_non_registered`);
	}
	if (data?.bl_profile !== null && isNaN(data.bl_profile)) {
		throw new TypeError(`SocketQuestionComment invalid bl_profile. Expected number, received ${typeof data.bl_profile}`);
	}
	if (data?.bl_profile_non_registered !== null && isNaN(data.bl_profile_non_registered)) {
		throw new TypeError(`SocketQuestionComment invalid bl_profile_non_registered. Expected number, received ${typeof data.bl_profile_non_registered}`);
	}
}

export function assertIsQuestionLike(data: unknown): asserts data is QuestionLikeData {
	if (!isObject(data)) {
		throw new TypeError(`QuestionLike data was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('question_id' in data) || isNaN(data.question_id)) {
		throw new TypeError(`QuestionLike requires a numeric question_id field`);
	}
}

export function assertIsQuestionStatusData(data: unknown): asserts data is QuestionStatusData {
	if (!isObject(data)) {
		throw new TypeError(`QuestionStatusData was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('id' in data) || isNaN(data.id)) {
		throw new TypeError(`QuestionStatusData invalid id field ${data?.id}`);
	}
	if (!('status' in data) || isNaN(data.status)) {
		throw new TypeError(`QuestionStatusData invalid status field ${data?.status}`);
	}
}

export function assertIsMessage(data: unknown): asserts data is Message<unknown> {
	if (!isObject(data)) {
		throw new TypeError(`Message was not of the correct type. Expected an object, received ${typeof data}`);
	}
	if (!('type' in data) || typeof data.type !== 'string') {
		throw new TypeError(`Message missing or invalid type field ${data?.type}`);
	}
	if (!('data' in data)) {
		throw new TypeError('Message missing data field');
	}
}
